import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Bell, AlertTriangle, Info, ArrowRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";

const SEVERITY_META = {
    high: {
        dot: "bg-rose-500",
        icon: AlertTriangle,
        text: "text-rose-700 dark:text-rose-300",
        bg: "bg-rose-500/10",
    },
    medium: {
        dot: "bg-amber-500",
        icon: AlertTriangle,
        text: "text-amber-700 dark:text-amber-300",
        bg: "bg-amber-500/10",
    },
    low: {
        dot: "bg-blue-500",
        icon: Info,
        text: "text-blue-700 dark:text-blue-300",
        bg: "bg-blue-500/10",
    },
};

const MODULE_LABEL = {
    fleet: "Fleet",
    equipment: "Machinery",
    inventory: "Inventory",
    aquaculture: "Aquaculture",
    incidents: "Incidents",
    admin: "Admin",
};

export default function AlertsBell({ variant = "desktop" }) {
    const { t } = useTranslation();
    const [data, setData] = useState({ alerts: [], total: 0, by_severity: {} });
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const r = await api.get("/alerts-unified");
            setData(r.data);
        } catch (e) {
            // silent — alerts are non-critical
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 60_000); // refresh every minute
        return () => clearInterval(t);
    }, []);

    // close on outside click
    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDown);
        return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    const total = data.total || 0;
    const high = data.by_severity?.high || 0;

    const jump = (url) => {
        setOpen(false);
        if (url) navigate(url);
    };

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="relative p-2 rounded-lg hover:bg-muted transition-colors"
                title={t("alerts.count", { count: total })}
                data-testid={`alerts-bell-${variant}`}
                aria-label={t("alerts.count", { count: total })}
            >
                <Bell className="w-5 h-5" />
                {total > 0 && (
                    <span
                        className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white grid place-items-center ${high > 0 ? "bg-rose-500" : "bg-amber-500"}`}
                        data-testid={`alerts-count-${variant}`}
                    >
                        {total > 99 ? "99+" : total}
                    </span>
                )}
            </button>

            {open && (
                <div
                    className="absolute right-0 top-full mt-2 w-96 max-w-[92vw] bg-popover border rounded-xl shadow-2xl z-50 overflow-hidden"
                    data-testid="alerts-dropdown"
                >
                    <div className="p-3 border-b flex items-center justify-between bg-muted/40">
                        <div>
                            <div className="font-semibold">{t("nav.alerts")}</div>
                            <div className="text-[11px] text-muted-foreground">
                                {total === 0 ? t("alerts.all_clear") : t("alerts.summary", { total, urgent: high })}
                            </div>
                        </div>
                        <button className="p-1 rounded hover:bg-muted" onClick={() => setOpen(false)}>
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                        {(data.alerts || []).length === 0 ? (
                            <div className="p-8 text-center text-sm text-muted-foreground">
                                {t("alerts.none")}
                            </div>
                        ) : (
                            (data.alerts || []).map((a) => {
                                const meta = SEVERITY_META[a.severity] || SEVERITY_META.low;
                                const ModIcon = meta.icon;
                                return (
                                    <button
                                        key={a.id}
                                        onClick={() => jump(a.action_url)}
                                        className="w-full text-left p-3 border-b last:border-0 hover:bg-muted/50 transition-colors flex items-start gap-3"
                                        data-testid={`alert-item-${a.id}`}
                                    >
                                        <div className={`shrink-0 w-8 h-8 rounded-lg ${meta.bg} grid place-items-center`}>
                                            <ModIcon className={`w-4 h-4 ${meta.text}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                                                <Badge variant="outline" className="text-[9px] uppercase tracking-wider">
                                                    {t(`alerts.modules.${a.module}`, MODULE_LABEL[a.module] || a.module)}
                                                </Badge>
                                            </div>
                                            <div className="font-semibold text-sm truncate mt-1">{a.title}</div>
                                            {a.description && (
                                                <div className="text-xs text-muted-foreground line-clamp-2">
                                                    {a.description}
                                                </div>
                                            )}
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
